// ─── AUTH ──────────────────────────────────────────────────────
const INITIAL_AUTH_TIMEOUT_MS = 3000;

const useAuth = () => {
  const [user, setUser]       = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchMe = useCallback(async ({ timeoutMs = 10000 } = {}) => {
    const controller = typeof AbortController !== 'undefined' && timeoutMs ? new AbortController() : null;
    const timeout = controller ? setTimeout(() => controller.abort(), timeoutMs) : null;
    try {
      const { ok, data } = await api('GET', '/api/auth/me', undefined, { signal: controller?.signal });
      if (ok) { setUser(data); return data; }
      return null;
    } catch {
      return null;
    } finally {
      if (timeout) clearTimeout(timeout);
    }
  }, []);

  useEffect(() => { fetchMe({ timeoutMs: INITIAL_AUTH_TIMEOUT_MS }).finally(() => setLoading(false)); }, [fetchMe]);

  const signup = async (email, password, intent) => {
    const { ok, data } = await api('POST', '/api/auth/signup', { email, password, intent });
    if (ok && data.pendingVerification) {
      window.OmnifillAnalytics?.trackLead?.({
        content_name: 'Account signup',
        status: 'pending_verification',
      });
      return { pendingVerification: true, email: data.email };
    }
    if (ok) { await fetchMe(); return { success: true }; }
    return { error: data.error || 'Signup failed' };
  };

  const login = async (identifier, password) => {
    const { ok, data } = await api('POST', '/api/auth/login', { identifier, password });
    if (ok && data.mfaRequired) return { mfaRequired: true, mfaToken: data.mfaToken, email: data.email };
    if (ok) { await fetchMe(); return { success: true }; }
    return { error: data.error || 'Login failed', code: data.code, email: data.email };
  };

  const verifyEmail = async (email, code) => {
    const { ok, data } = await api('POST', '/api/auth/verify-email', { email, code });
    if (ok && data.mfaRequired) return { mfaRequired: true, mfaToken: data.mfaToken, email: data.email };
    if (ok) {
      window.OmnifillAnalytics?.trackRegistration?.({
        content_name: 'Email verification',
      });
      await fetchMe();
      return { success: true };
    }
    return { error: data.error || 'Verification failed' };
  };

  const resendVerification = async (email) => {
    const { ok, data } = await api('POST', '/api/auth/resend-verification', { email });
    if (ok) return { success: true };
    return { error: data.error || 'Could not resend code' };
  };

  const setPassword = async (password) => {
    const { ok, data } = await api('POST', '/api/auth/password', { password });
    if (ok) { await fetchMe(); return { success: true }; }
    return { error: data.error || 'Could not save password' };
  };

  const forgotPassword = async (email) => {
    const { ok, data } = await api('POST', '/api/auth/forgot-password', { email });
    if (ok) return { success: true };
    return { error: data.error || 'Could not send reset code' };
  };

  const resetPassword = async (email, code, password) => {
    const { ok, data } = await api('POST', '/api/auth/reset-password', { email, code, password });
    if (ok && data.mfaRequired) return { mfaRequired: true, mfaToken: data.mfaToken, email: data.email };
    if (ok) { await fetchMe(); return { success: true }; }
    return { error: data.error || 'Could not reset password' };
  };

  const verifyMfa = async ({ mfaToken, code, recoveryCode }) => {
    const { ok, data } = await api('POST', '/api/auth/2fa/verify', { mfaToken, code, recoveryCode });
    if (ok) { await fetchMe(); return { success: true }; }
    return { error: data.error || 'Could not verify 2FA code' };
  };

  const transferAuth = async token => {
    const { ok, data } = await api('POST', '/api/auth/transfer', { token });
    if (ok) { await fetchMe(); return { success: true }; }
    return { error: data.error || 'Google sign-in failed. Please try again.' };
  };

  const deleteAccount = async () => {
    const { ok, data } = await api('DELETE', '/api/auth/account');
    if (ok) { setUser(null); return { success: true }; }
    return { error: data.error || 'Could not delete account' };
  };

  const logout = async () => {
    await api('POST', '/api/auth/logout');
    setUser(null);
  };

  return { user, loading, signup, login, verifyEmail, verifyMfa, transferAuth, resendVerification, setPassword, forgotPassword, resetPassword, deleteAccount, logout, refreshAuth: fetchMe };
};

const AuthScreen = ({ onSignup, onLogin, onVerifyEmail, onVerifyMfa, onTransferAuth, onResendVerification, onForgotPassword, onResetPassword, portal = 'dashboard' }) => {
  const isAdminPortal = portal === 'admin';
  const [trialIntent]           = useState(() => new URLSearchParams(window.location.search).get('onboarding') === 'trial');
  const [pendingSwitchAccount]  = useState(() => consumePendingAccountSwitch());
  const [tab, setTab]           = useState(() => pendingSwitchAccount ? 'signin' : (!isAdminPortal && trialIntent) ? 'signup' : 'signin');
  const [email, setEmail]       = useState(() => pendingSwitchAccount?.email || '');
  const [password, setPassword] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [pendingEmail, setPendingEmail] = useState('');
  const [forgotMode, setForgotMode] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [resetPassword, setResetPassword] = useState('');
  const [resetConfirm, setResetConfirm] = useState('');
  const [mfaToken, setMfaToken] = useState('');
  const [mfaEmail, setMfaEmail] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [mfaRecoveryCode, setMfaRecoveryCode] = useState('');
  const [mfaRecoveryMode, setMfaRecoveryMode] = useState(false);
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(false);
  const [notice, setNotice]     = useState('');

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const authError = params.get('auth_error');
    const queryMfaToken = params.get('mfa_token');
    const transferToken = params.get('auth_transfer');
    const cleanAuthParams = () => {
      ['auth_error', 'mfa', 'mfa_token', 'auth_transfer'].forEach(key => params.delete(key));
      const query = params.toString();
      const nextUrl = window.location.pathname + (query ? '?' + query : '') + window.location.hash;
      window.history.replaceState({}, '', nextUrl);
    };
    if (queryMfaToken) {
      setMfaToken(queryMfaToken);
      setNotice('Enter the 6-digit code from your authenticator app.');
      cleanAuthParams();
      return;
    }
    if (transferToken) {
      if (onTransferAuth) {
        cleanAuthParams();
        setLoading(true);
        onTransferAuth(transferToken).then(result => {
          if (result?.error) setError(result.error);
        }).finally(() => setLoading(false));
        return;
      }
    }
    if (!authError) return;
    const messages = {
      google_not_configured: 'Google sign-in is not configured yet.',
      missing_google_params: 'Google sign-in did not return the expected details.',
      invalid_google_state: 'Google sign-in expired. Please try again.',
      google_profile_unavailable: 'Google did not return an email address.',
      google_email_unverified: 'Please verify your Google email address before signing in.',
      google_account_conflict: 'That email is already linked to a different Google account.',
      google_server_error: 'Google sign-in failed. Please try again.',
    };
    setError(messages[authError] || 'Google sign-in failed. Please try again.');
    cleanAuthParams();
  }, []);

  const reset     = () => { setEmail(''); setPassword(''); setVerificationCode(''); setPendingEmail(''); setForgotMode(false); setResetSent(false); setResetEmail(''); setResetCode(''); setResetPassword(''); setResetConfirm(''); setMfaToken(''); setMfaEmail(''); setMfaCode(''); setMfaRecoveryCode(''); setMfaRecoveryMode(false); setError(''); setNotice(''); };
  const switchTab = t  => { setTab(isAdminPortal ? 'signin' : t); reset(); };

  const handleMfaRequired = result => {
    setMfaToken(result.mfaToken || '');
    setMfaEmail(result.email || email || resetEmail || '');
    setMfaCode('');
    setMfaRecoveryCode('');
    setMfaRecoveryMode(false);
    setForgotMode(false);
    setPendingEmail('');
    setNotice('Enter the 6-digit code from your authenticator app.');
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setError('');
    setLoading(true);
    let result;
    if (tab === 'signup' && !isAdminPortal) {
      if (!email.trim())       { setError('Email is required');                      setLoading(false); return; }
      if (password.length < 10) { setError('Password must be at least 10 characters'); setLoading(false); return; }
      result = await onSignup(email.trim(), password, null);
    } else {
      if (!email.trim()) { setError('Email is required');       setLoading(false); return; }
      if (!password)     { setError('Password is required');    setLoading(false); return; }
      result = await onLogin(email.trim(), password);
    }
    if (result?.pendingVerification) {
      setPendingEmail(result.email);
      setNotice('We sent a 6-digit verification code to your email.');
    } else if (result?.code === 'email_unverified') {
      setPendingEmail(result.email || email.trim());
      setNotice('Enter the verification code we sent to your email.');
    } else if (result?.mfaRequired) {
      handleMfaRequired(result);
    } else if (result?.error) setError(result.error);
    setLoading(false);
  };

  const handleVerify = async e => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const result = await onVerifyEmail(pendingEmail, verificationCode);
    if (result?.mfaRequired) handleMfaRequired(result);
    else if (result?.error) setError(result.error);
    setLoading(false);
  };

  const handleResend = async () => {
    setError('');
    setNotice('');
    setLoading(true);
    const result = await onResendVerification(pendingEmail);
    if (result?.error) setError(result.error);
    else setNotice('A new verification code is on the way.');
    setLoading(false);
  };

  const openForgot = () => {
    setForgotMode(true);
    setResetEmail(email.trim());
    setResetSent(false);
    setError('');
    setNotice('');
  };

  const handleForgot = async e => {
    e.preventDefault();
    setError('');
    setNotice('');
    if (!resetEmail.trim()) return setError('Email is required');
    setLoading(true);
    const result = await onForgotPassword(resetEmail.trim());
    if (result?.error) setError(result.error);
    else {
      setResetSent(true);
      setNotice('If that email has an Omnifill account, a reset code is on the way.');
    }
    setLoading(false);
  };

  const handleResetPassword = async e => {
    e.preventDefault();
    setError('');
    if (resetCode.length !== 6) return setError('Enter the 6-digit reset code');
    if (resetPassword.length < 10) return setError('Password must be at least 10 characters');
    if (resetPassword !== resetConfirm) return setError('Passwords do not match');
    setLoading(true);
    const result = await onResetPassword(resetEmail.trim(), resetCode, resetPassword);
    if (result?.mfaRequired) handleMfaRequired(result);
    else if (result?.error) setError(result.error);
    setLoading(false);
  };

  const handleMfaSubmit = async e => {
    e.preventDefault();
    setError('');
    const cleanCode = mfaCode.replace(/\D/g, '');
    if (!mfaRecoveryMode && cleanCode.length !== 6) return setError('Enter the 6-digit authenticator code');
    if (mfaRecoveryMode && !mfaRecoveryCode.trim()) return setError('Enter a recovery code');
    setLoading(true);
    const result = await onVerifyMfa({
      mfaToken,
      code: mfaRecoveryMode ? '' : cleanCode,
      recoveryCode: mfaRecoveryMode ? mfaRecoveryCode : '',
    });
    if (result?.error) setError(result.error);
    setLoading(false);
  };

  const inp = { width:'100%', border:'1px solid #E5E7EB', borderRadius:8, padding:'10px 14px', fontSize:14, outline:'none', background:'#fff', color:'#1A1A2E' };
  const lbl = { fontSize:12, fontWeight:600, color:'#374151', display:'block', marginBottom:6 };
  const googleBtn = { width:'100%', background:'#fff', color:'#374151', border:'1px solid #D1D5DB', borderRadius:8, padding:'10px 12px', fontSize:14, fontWeight:700, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:10 };
  const googleHref = isAdminPortal ? '/api/auth/google?portal=admin' : '/api/auth/google';
  const googleLogo = <img src="/assets/google-g-logo.webp" alt="" style={{ width:18, height:18, objectFit:'contain', flexShrink:0 }}/>;
  const prefillNotice = pendingSwitchAccount?.email || '';
  const headerHref = isAdminPortal ? 'https://dashboard.omnifill.net' : 'https://omnifill.net';
  const headerLabel = isAdminPortal ? 'Dashboard' : 'Homepage';
  const authTabs = isAdminPortal ? ['signin'] : ['signin','signup'];
  const headingText = forgotMode ? 'Reset your password' : tab === 'signup' ? 'Create your account' : 'Welcome back';
  const subheadingText = forgotMode
    ? 'Enter your email and we will send a reset code.'
    : tab === 'signup'
      ? 'Create your Omnifill account, then start the 14-day trial through Stripe.'
      : isAdminPortal ? 'Sign in to access the admin portal.' : 'Sign in to access your dashboard.';

  return (
    <div className="auth-screen">
      <div className="auth-background" aria-hidden="true">
        <picture className="auth-bg-panel auth-bg-panel-commerce">
          <source type="image/webp" srcSet="/assets/stock/client_1-960.webp 960w, /assets/stock/client_1-1800.webp 1800w" sizes="(max-width: 900px) 100vw, 55vw"/>
          <img className="auth-bg-image" src="/assets/stock/client_1-1800.webp" alt="" width="1800" height="1200" decoding="async" fetchPriority="low"/>
        </picture>
        <picture className="auth-bg-panel auth-bg-panel-fulfillment">
          <source type="image/webp" srcSet="/assets/stock/truck_2-960.webp 960w, /assets/stock/truck_2-1800.webp 1800w" sizes="56vw"/>
          <img className="auth-bg-image" src="/assets/stock/truck_2-1800.webp" alt="" width="1800" height="1013" decoding="async" fetchPriority="low"/>
        </picture>
      </div>
      <nav className="auth-nav">
        <a href={headerHref} style={{ display:'flex' }}><Logo size={19} suffix={isAdminPortal ? 'admin portal' : ''}/></a>
        <a href={headerHref} style={{ background:'none', border:'none', cursor:'pointer', fontSize:13, color:'#6B7280', display:'flex', alignItems:'center', gap:5, textDecoration:'none' }}>
          <ChevronLeft size={14} strokeWidth={2}/> {headerLabel}
        </a>
      </nav>

      <div className="auth-stage">
        <div className="auth-card">

          {!isAdminPortal && <div style={{ display:'flex', background:'#F3F4F6', borderRadius:10, padding:4, marginBottom:28 }}>
            {authTabs.map(t => (
              <button key={t} onClick={() => switchTab(t)} style={{
                flex:1, border:'none', borderRadius:7, padding:'8px',
                background: tab===t ? '#fff' : 'transparent',
                boxShadow: tab===t ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                fontSize:13, fontWeight: tab===t ? 600 : 400,
                color: tab===t ? '#1A1A2E' : '#6B7280', cursor:'pointer', transition:'all 0.15s',
              }}>
                {t === 'signin' ? 'Sign in' : 'Sign up'}
              </button>
            ))}
          </div>}

          <h2 style={{ fontSize:20, fontWeight:700, letterSpacing:'-0.3px', marginBottom:6 }}>
            {headingText}
          </h2>
          <p style={{ fontSize:13, color:'#9CA3AF', marginBottom:24 }}>
            {subheadingText}
          </p>
          {prefillNotice && !forgotMode && !pendingEmail && tab === 'signin' && (
            <div style={{ background:'#EEF6FF', border:'1px solid #BFDBFE', borderRadius:8, padding:'10px 14px', fontSize:13, color:'#1D4ED8', marginBottom:16 }}>
              Switching to <strong>{prefillNotice}</strong>
            </div>
          )}

          {mfaToken ? (
            <form onSubmit={handleMfaSubmit} style={{ display:'flex', flexDirection:'column', gap:14 }}>
              {notice && (
                <div style={{ background:'#EEF6FF', border:'1px solid #BFDBFE', borderRadius:8, padding:'10px 14px', fontSize:13, color:'#1D4ED8' }}>
                  {notice}
                </div>
              )}
              {mfaEmail && <div style={{ fontSize:13, color:'#6B7280' }}>Signing in as <strong>{mfaEmail}</strong></div>}
              {mfaRecoveryMode ? (
                <div>
                  <label style={lbl}>Recovery code</label>
                  <input type="text" autoComplete="one-time-code" value={mfaRecoveryCode} onChange={e => setMfaRecoveryCode(e.target.value.toUpperCase())}
                    placeholder="ABCD-EFGH" style={{ ...inp, letterSpacing:'0.08em', fontWeight:700, textAlign:'center' }}/>
                </div>
              ) : (
                <div>
                  <label style={lbl}>Authenticator code</label>
                  <input type="text" inputMode="numeric" autoComplete="one-time-code" value={mfaCode} onChange={e => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="123456" style={{ ...inp, letterSpacing:'0.18em', fontWeight:700, textAlign:'center' }}/>
                </div>
              )}
              {error && (
                <div style={{ background:'#FEE2E2', border:'1px solid #FECACA', borderRadius:8, padding:'10px 14px', fontSize:13, color:'#991B1B' }}>
                  {error}
                </div>
              )}
              <button type="submit" disabled={loading} style={{
                background: loading ? '#93C5FD' : '#006FDD', color:'#fff', border:'none',
                borderRadius:8, padding:'11px', fontSize:14, fontWeight:700,
                cursor: loading ? 'not-allowed' : 'pointer',
              }}>
                {loading ? '…' : 'Verify and sign in'}
              </button>
              <button type="button" onClick={() => { setMfaRecoveryMode(value => !value); setError(''); }} style={{ background:'none', border:'none', color:'#006FDD', cursor:'pointer', fontSize:13, fontWeight:700 }}>
                {mfaRecoveryMode ? 'Use authenticator code' : 'Use recovery code'}
              </button>
              <button type="button" onClick={reset} style={{ background:'none', border:'none', color:'#6B7280', cursor:'pointer', fontSize:13, fontWeight:700 }}>
                Back to sign in
              </button>
            </form>
          ) : forgotMode ? (
            <form onSubmit={resetSent ? handleResetPassword : handleForgot} style={{ display:'flex', flexDirection:'column', gap:14 }}>
              {notice && (
                <div style={{ background:'#EEF6FF', border:'1px solid #BFDBFE', borderRadius:8, padding:'10px 14px', fontSize:13, color:'#1D4ED8' }}>
                  {notice}
                </div>
              )}
              <div>
                <label style={lbl}>Email</label>
                <input type="email" value={resetEmail} onChange={e => setResetEmail(e.target.value)}
                  placeholder="you@example.com" style={inp} disabled={resetSent}/>
              </div>
              {resetSent && (
                <>
                  <div>
                    <label style={lbl}>Reset code</label>
                    <input type="text" inputMode="numeric" autoComplete="one-time-code" value={resetCode} onChange={e => setResetCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="123456" style={{ ...inp, letterSpacing:'0.18em', fontWeight:700, textAlign:'center' }}/>
                  </div>
                  <div>
                    <label style={lbl}>New password</label>
                    <input type="password" autoComplete="new-password" value={resetPassword} onChange={e => setResetPassword(e.target.value)}
                      placeholder="At least 10 characters" style={inp}/>
                  </div>
                  <div>
                    <label style={lbl}>Confirm password</label>
                    <input type="password" autoComplete="new-password" value={resetConfirm} onChange={e => setResetConfirm(e.target.value)}
                      placeholder="Repeat password" style={inp}/>
                  </div>
                </>
              )}
              {error && (
                <div style={{ background:'#FEE2E2', border:'1px solid #FECACA', borderRadius:8, padding:'10px 14px', fontSize:13, color:'#991B1B' }}>
                  {error}
                </div>
              )}
              <button type="submit" disabled={loading} style={{
                background: loading ? '#93C5FD' : '#006FDD', color:'#fff', border:'none',
                borderRadius:8, padding:'11px', fontSize:14, fontWeight:700,
                cursor: loading ? 'not-allowed' : 'pointer',
              }}>
                {loading ? '…' : resetSent ? 'Reset password' : 'Send reset code'}
              </button>
              {resetSent && (
                <button type="button" onClick={handleForgot} disabled={loading} style={{ background:'none', border:'none', color:'#006FDD', cursor: loading ? 'wait' : 'pointer', fontSize:13, fontWeight:700 }}>
                  Resend code
                </button>
              )}
              <button type="button" onClick={reset} style={{ background:'none', border:'none', color:'#6B7280', cursor:'pointer', fontSize:13, fontWeight:700 }}>
                Back to sign in
              </button>
            </form>
          ) : pendingEmail ? (
            <form onSubmit={handleVerify} style={{ display:'flex', flexDirection:'column', gap:14 }}>
              {notice && (
                <div style={{ background:'#EEF6FF', border:'1px solid #BFDBFE', borderRadius:8, padding:'10px 14px', fontSize:13, color:'#1D4ED8' }}>
                  {notice}
                </div>
              )}
              <div>
                <label style={lbl}>Verification code</label>
                <input type="text" inputMode="numeric" autoComplete="one-time-code" value={verificationCode} onChange={e => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="123456" style={{ ...inp, letterSpacing:'0.18em', fontWeight:700, textAlign:'center' }}/>
              </div>
              {error && (
                <div style={{ background:'#FEE2E2', border:'1px solid #FECACA', borderRadius:8, padding:'10px 14px', fontSize:13, color:'#991B1B' }}>
                  {error}
                </div>
              )}
              <button type="submit" disabled={loading || verificationCode.length !== 6} style={{
                background: loading || verificationCode.length !== 6 ? '#93C5FD' : '#006FDD', color:'#fff', border:'none',
                borderRadius:8, padding:'11px', fontSize:14, fontWeight:700,
                cursor: loading || verificationCode.length !== 6 ? 'not-allowed' : 'pointer',
              }}>
                {loading ? '…' : 'Verify email'}
              </button>
              <button type="button" onClick={handleResend} disabled={loading} style={{ background:'none', border:'none', color:'#006FDD', cursor: loading ? 'wait' : 'pointer', fontSize:13, fontWeight:700 }}>
                Resend code
              </button>
            </form>
          ) : (
            <>
          <button type="button" onClick={() => { window.location.href = googleHref; }} style={googleBtn}>
            {googleLogo}
            {tab === 'signup' ? 'Sign up with Google' : 'Continue with Google'}
          </button>
          {tab === 'signup' && (
            <p style={{ margin:'10px 0 0', color:'#6B7280', fontSize:11.5, lineHeight:1.55, textAlign:'center' }}>
              By creating an account, you agree to the{' '}
              <a href="/terms-of-service" target="_blank" rel="noreferrer" style={{ color:'#006FDD', fontWeight:700, textDecoration:'none' }}>Terms</a>,{' '}
              <a href="/privacy-policy" target="_blank" rel="noreferrer" style={{ color:'#006FDD', fontWeight:700, textDecoration:'none' }}>Privacy Policy</a>, and{' '}
              <a href="/data-processing-agreement" target="_blank" rel="noreferrer" style={{ color:'#006FDD', fontWeight:700, textDecoration:'none' }}>Data Processing Agreement</a>.
            </p>
          )}

          <div style={{ display:'flex', alignItems:'center', gap:12, margin:'18px 0', color:'#C4C9D1', fontSize:12, fontWeight:600 }}>
            <span style={{ height:1, background:'#E5E7EB', flex:1 }}/>
            <span>or</span>
            <span style={{ height:1, background:'#E5E7EB', flex:1 }}/>
          </div>

          <form onSubmit={handleSubmit} style={{ display:'flex', flexDirection:'column', gap:14 }}>
            <div>
              <label style={lbl}>Email</label>
              <input type="email" autoComplete="email" value={email} onChange={e => setEmail(e.target.value)}
                placeholder="you@example.com" style={inp}/>
            </div>

            <div>
              <label style={lbl}>Password</label>
              <input type="password" value={password} onChange={e => setPassword(e.target.value)}
                placeholder={tab === 'signup' ? 'At least 10 characters' : '••••••••'} style={inp}/>
            </div>

            {error && (
              <div style={{ background:'#FEE2E2', border:'1px solid #FECACA', borderRadius:8, padding:'10px 14px', fontSize:13, color:'#991B1B' }}>
                {error}
              </div>
            )}

            <button type="submit" disabled={loading} style={{
              background: loading ? '#93C5FD' : '#006FDD', color:'#fff', border:'none',
              borderRadius:8, padding:'11px', fontSize:14, fontWeight:700,
              cursor: loading ? 'not-allowed' : 'pointer', marginTop:4, transition:'background 0.15s',
            }}>
              {loading ? '…' : tab === 'signin' ? 'Sign in' : 'Create account'}
            </button>
            {tab === 'signin' && (
              <button type="button" onClick={openForgot} style={{ background:'none', border:'none', color:'#006FDD', cursor:'pointer', fontSize:13, fontWeight:700, padding:0 }}>
                Forgot password?
              </button>
            )}
          </form>

          {error && trialIntent && tab === 'signup' && (
            <div style={{ background:'#FEE2E2', border:'1px solid #FECACA', borderRadius:8, padding:'10px 14px', fontSize:13, color:'#991B1B', marginTop:14 }}>
              {error}
            </div>
          )}
            </>
          )}

          {!pendingEmail && !isAdminPortal && <div style={{ textAlign:'center', marginTop:20, fontSize:13, color:'#9CA3AF' }}>
            {tab === 'signin' ? "Don't have an account? " : 'Already have an account? '}
            <button onClick={() => switchTab(tab === 'signin' ? 'signup' : 'signin')}
              style={{ background:'none', border:'none', color:'#006FDD', cursor:'pointer', fontSize:13, fontWeight:600, padding:0 }}>
              {tab === 'signin' ? 'Sign up' : 'Sign in'}
            </button>
          </div>}
        </div>
      </div>
    </div>
  );
};

// ─── SUBSCRIPTION WALL ─────────────────────────────────────────

const SubscriptionWall = ({ user, onLogout, onRefresh }) => {
  const [checking, setChecking] = useState(false);
  const [checked,  setChecked]  = useState(false);
  const [checkoutPlan, setCheckoutPlan] = useState('');
  const [checkoutError, setCheckoutError] = useState('');
  const [rememberedAccounts, setRememberedAccounts] = useState(() => rememberAccount(user));
  const currentAccountKey = user?.email || '';
  const otherAccounts = rememberedAccounts.filter(account => account.email && account.email !== currentAccountKey).slice(0, 3);
  const canManageBilling = !!user?.subscription?.canManageBilling;
  const showBillingPortalInsteadOfCheckout = canManageBilling && user?.subscription?.status === 'past_due';

  useEffect(() => {
    setRememberedAccounts(rememberAccount(user));
  }, [user?.email, user?.isAdmin]);

  const handleRefresh = async () => {
    setChecking(true);
    await onRefresh();
    setChecking(false);
    setChecked(true);
  };

  const startCheckout = async (plan) => {
    setCheckoutPlan(plan);
    setCheckoutError('');
    try {
      const { ok, data } = await api('POST', '/api/billing/checkout', { plan });
      if (ok && data.url) {
        window.location.href = data.url;
        return;
      }
      setCheckoutError(data?.error || 'Could not start checkout. Please try again.');
      setCheckoutPlan('');
    } catch {
      setCheckoutError('Could not start checkout. Please try again.');
      setCheckoutPlan('');
    }
  };

  const openBillingPortal = async () => {
    setCheckoutPlan('portal');
    setCheckoutError('');
    try {
      const { ok, data } = await api('POST', '/api/billing/portal');
      if (ok && data.url) {
        window.location.href = data.url;
        return;
      }
      setCheckoutError(data?.error || 'Could not open billing portal. Please try again.');
      setCheckoutPlan('');
    } catch {
      setCheckoutError('Could not open billing portal. Please try again.');
      setCheckoutPlan('');
    }
  };

  const switchAccount = async account => {
    setPendingAccountSwitch(account);
    await onLogout();
  };

  const addAccount = async () => {
    clearPendingAccountSwitch();
    await onLogout();
  };

  return (
    <div style={{ minHeight:'100vh', background:'#F8F9FA', display:'flex', flexDirection:'column' }}>
      <nav style={{ background:'#fff', borderBottom:'1px solid #E5E7EB', minHeight:60, display:'flex', alignItems:'center', justifyContent:'space-between', gap:18, padding:'12px clamp(20px, 5vw, 48px)', flexWrap:'wrap' }}>
        <a href="https://omnifill.net" style={{ display:'flex' }}><Logo size={19}/></a>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'flex-end', gap:22, minWidth:0, flex:'1 1 260px' }}>
          <div style={{ fontSize:13, color:'#6B7280', minWidth:0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
            Logged in as <strong style={{ color:'#1A1A2E', fontWeight:700 }}>{user.email}</strong>
          </div>
          <button className="subscription-signout" onClick={onLogout} style={{ background:'none', border:'none', cursor:'pointer', fontSize:13, flexShrink:0, display:'flex', alignItems:'center', gap:6, padding:'6px 0' }}>
            <LogOut size={14} strokeWidth={1.8}/>
            Sign out
          </button>
        </div>
      </nav>

      <div style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', padding:'48px 24px' }}>
        <div style={{ maxWidth:520, width:'100%' }}>
	          <div style={{ textAlign:'center', marginBottom:36 }}>
	            <div style={{ fontSize:22, fontWeight:800, letterSpacing:'-0.4px', color:'#1A1A2E', marginBottom:10 }}>
	              {showBillingPortalInsteadOfCheckout ? 'Manage Omnifill Pro' : 'Start Omnifill Pro'}
	            </div>
	            <p style={{ fontSize:14, color:'#6B7280', lineHeight:1.6 }}>
	              {showBillingPortalInsteadOfCheckout
                  ? <>Update billing or payment method through Stripe for <strong>{user.email}</strong>.</>
                  : <>Pro unlocks unlimited orders, analytics, automatic fulfillment routing, and support for <strong>{user.email}</strong>.</>}
	            </p>
	          </div>

          {showBillingPortalInsteadOfCheckout ? (
            <div style={{ background:'#fff', border:'1.5px solid #006FDD', borderRadius:14, padding:24, marginBottom:20, display:'grid', gap:12 }}>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:12 }}>
                <div>
                  <div style={{ fontSize:13, fontWeight:800, color:'#374151' }}>Stripe billing portal</div>
                  <div style={{ fontSize:12, color:'#6B7280', marginTop:4, lineHeight:1.45 }}>Switch monthly or annual, update payment method, or review your subscription.</div>
                </div>
                <DollarSign size={24} strokeWidth={2.2} color="#006FDD"/>
              </div>
              <button onClick={openBillingPortal} disabled={!!checkoutPlan} style={{ display:'inline-flex', alignItems:'center', justifyContent:'center', gap:7, width:'100%', background:'#006FDD', color:'#fff', border:'none', borderRadius:8, padding:'10px', fontSize:13, fontWeight:800, cursor:checkoutPlan ? 'wait' : 'pointer', textAlign:'center' }}>
                {checkoutPlan === 'portal' ? 'Opening...' : 'Manage billing'} <ArrowRight size={14} strokeWidth={2.4}/>
              </button>
            </div>
          ) : (
	          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14, marginBottom:20 }}>
	            {[
	              { label:'Monthly', price:'$29', period:'/mo', plan:'monthly' },
	              { label:'Annual',  price:'$290', period:'/yr', plan:'yearly', badge:'BEST VALUE' },
	            ].map(({ label, price, period, plan, badge }) => (
	              <div key={label} style={{ background:'#fff', border:`1.5px solid ${badge ? '#006FDD' : '#E5E7EB'}`, borderRadius:14, padding:24, display:'flex', flexDirection:'column', gap:12 }}>
                <div style={{ background:'#EEF6FF', border:'1px solid #BFDBFE', color:'#075CBA', borderRadius:8, padding:'7px 9px', fontSize:11, fontWeight:800, textAlign:'center' }}>
                  14 day free trial
                </div>
                <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                  <span style={{ fontSize:13, fontWeight:600, color:'#374151' }}>{label}</span>
                  {badge && <span style={{ background:'#EEF4FF', color:'#006FDD', fontSize:9, fontWeight:700, letterSpacing:'0.08em', padding:'2px 7px', borderRadius:4 }}>{badge}</span>}
                </div>
	                <div style={{ fontSize:32, fontWeight:800, letterSpacing:'-1px' }}>
	                  {price}<span style={{ fontSize:14, color:'#6B7280', fontWeight:400 }}>{period}</span>
	                </div>
	                <button onClick={() => startCheckout(plan)} disabled={!!checkoutPlan} style={{ display:'block', width:'100%', background: badge ? '#006FDD' : '#1E2433', color:'#fff', border:'none', borderRadius:8, padding:'10px', fontSize:13, fontWeight:600, cursor: checkoutPlan ? 'wait' : 'pointer', textAlign:'center', marginTop:'auto' }}>
	                  {checkoutPlan === plan ? 'Opening checkout...' : 'Start trial'}
	                </button>
	              </div>
	            ))}
	          </div>
          )}
          {checkoutError && <div style={{ color:'#B91C1C', fontSize:12, fontWeight:700, margin:'-8px 0 16px', lineHeight:1.4 }}>{checkoutError}</div>}

          <div style={{ background:'#fff', border:'1px solid #E5E7EB', borderRadius:12, padding:'16px 20px', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
            <div>
              <div style={{ fontSize:13, fontWeight:600, color:'#374151' }}>Already paid?</div>
              <div style={{ fontSize:12, color:'#9CA3AF', marginTop:2 }}>
                {checked ? 'Subscription status refreshed.' : 'After completing checkout, refresh to activate.'}
              </div>
            </div>
            <button onClick={handleRefresh} disabled={checking} style={{ background:'#F3F4F6', color:'#374151', border:'none', borderRadius:8, padding:'8px 16px', fontSize:13, fontWeight:600, cursor: checking ? 'wait' : 'pointer', display:'flex', alignItems:'center', gap:7, flexShrink:0 }}>
              <RefreshCw size={13} strokeWidth={2} style={{ animation: checking ? 'spin 1s linear infinite' : 'none' }}/> Refresh status
            </button>
          </div>

          <div style={{ marginTop:14, background:'#fff', border:'1px solid #E5E7EB', borderRadius:12, padding:'14px 16px' }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:12, marginBottom: otherAccounts.length ? 10 : 0 }}>
              <div>
                <div style={{ fontSize:13, fontWeight:700, color:'#374151' }}>Wrong account?</div>
                <div style={{ fontSize:12, color:'#9CA3AF', marginTop:2 }}>Switch sign-in before starting checkout.</div>
              </div>
              <button type="button" onClick={addAccount} style={{ background:'#F3F4F6', color:'#374151', border:'none', borderRadius:8, padding:'8px 12px', fontSize:13, fontWeight:700, cursor:'pointer', flexShrink:0 }}>
                Add account
              </button>
            </div>
            {otherAccounts.length > 0 && (
              <div style={{ display:'grid', gap:7 }}>
                {otherAccounts.map(account => (
                  <button key={account.email} type="button" onClick={() => switchAccount(account)} style={{ width:'100%', border:'1px solid #EEF2F7', background:'#F8FAFC', borderRadius:8, padding:'9px 10px', display:'flex', alignItems:'center', gap:8, textAlign:'left', cursor:'pointer' }}>
                    <UserCircle size={16} strokeWidth={1.8} color="#6B7280"/>
                    <span style={{ minWidth:0, flex:1 }}>
                      <span style={{ display:'block', fontSize:12, fontWeight:800, color:'#111827', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{account.email}</span>
                      <span style={{ display:'block', fontSize:11, color:'#6B7280', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>Saved account</span>
                    </span>
                    <ArrowRight size={13} strokeWidth={2.2} color="#9CA3AF"/>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
